// TokkoService.js
// Conexión segura a través del backend propio
// Versión que usa el backend para proteger la API key de Tokko

import axios from "axios";

class TokkoService {
  constructor() {
    // Debug: verificar variables de entorno
    console.log("🔍 Variables de entorno:");
    console.log("- REACT_APP_API_URI:", process.env.REACT_APP_API_URI);
    console.log("- NODE_ENV:", process.env.NODE_ENV);

    // Usar nuestro backend en lugar de conectar directamente a Tokko
    // En desarrollo usará localhost, en producción la URL de producción
    this.backendUrl = process.env.REACT_APP_API_URI || "http://localhost/web/";
    this.tokkoEndpoint = "tokko/contact"; // Endpoint sin barra final

    // Debug: verificar configuración
    console.log("🌐 Backend URL configurada:", this.backendUrl);
    console.log("🔗 Tokko Endpoint:", this.tokkoEndpoint);
    console.log("📡 Modo: Backend Proxy (Seguro)");

    // Guardar los UTM del aterrizaje antes de que el router borre el query string
    this.captureUTMParams();
  }

  /**
   * Guarda los UTM del aterrizaje en sessionStorage.
   *
   * El sitio es una SPA: si el visitante llega con ?utm_source=... y navega a
   * otra vista antes de enviar el formulario, el query string ya no existe.
   * Leerlos recién al enviar perdía la atribución de toda campaña cuyo lead no
   * completara el formulario en la misma URL de aterrizaje.
   */
  captureUTMParams() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const found = {};

      TokkoService.UTM_KEYS.forEach((key) => {
        const value = urlParams.get(`utm_${key}`);
        if (value) found[key] = value;
      });

      // Una visita sin UTM no pisa los de la campaña que trajo al visitante
      if (Object.keys(found).length > 0) {
        sessionStorage.setItem(TokkoService.UTM_STORAGE_KEY, JSON.stringify(found));
      }
    } catch (error) {
      // Modo incógnito o storage bloqueado: seguimos sin atribución, no rompemos el form
      console.warn("No se pudieron guardar los UTM:", error);
    }
  }

  /**
   * Devuelve los UTM guardados, con "0" para los ausentes (formato que espera el backend)
   */
  getUTMParams() {
    let stored = {};

    try {
      stored = JSON.parse(sessionStorage.getItem(TokkoService.UTM_STORAGE_KEY) || "{}");
    } catch (error) {
      stored = {};
    }

    return TokkoService.UTM_KEYS.reduce((utm, key) => {
      utm[key] = stored[key] || "0";
      return utm;
    }, {});
  }

  /**
   * Construye la URL del endpoint backend
   */
  getApiUrl() {
    // Asegurar que la URL termine correctamente
    let baseUrl = this.backendUrl;
    if (!baseUrl.endsWith("/")) {
      baseUrl += "/";
    }

    const fullUrl = `${baseUrl}${this.tokkoEndpoint}`;
    console.log("🔧 URL del backend:", fullUrl);
    return fullUrl;
  }

  /**
   * Test de conexión al backend
   */
  async testConnection() {
    try {
      console.log("🧪 Probando conexión al backend...");

      const testData = {
        test: true,
        timestamp: new Date().toISOString(),
      };

      console.log("📤 Datos de test:", testData);
      console.log("🔗 URL de test:", this.getApiUrl());

      const response = await axios.post(this.getApiUrl(), testData, {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 10000,
      });

      console.log("✅ Test exitoso:", response);
      return {
        success: true,
        message: "Conexión al backend exitosa",
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error("❌ Error en test:", error);

      let errorMessage = "Error de conexión con el servidor";
      if (error.code === "ECONNABORTED") {
        errorMessage = "Timeout - El servidor tardó demasiado en responder";
      } else if (error.response) {
        errorMessage = `Error del servidor: ${error.response.status} - ${error.response.statusText}`;
      } else if (error.request) {
        errorMessage = "No se pudo conectar al servidor";
      }

      return {
        success: false,
        message: errorMessage,
        error: error.message,
        details: {
          code: error.code,
          status: error.response?.status,
          url: this.getApiUrl(),
        },
      };
    }
  }

  /**
   * Envía el contacto a través del backend (método principal)
   * @param {Object} contactData - Datos del contacto (nombre, email, telefono, mensaje)
   * @param {Object} propertyData - Datos opcionales de la propiedad
   * @param {String} origen - Identificador del origen del formulario (ej: 'footer', 'proyecto', 'terreno')
   */
  async sendContact(contactData, propertyData = null, origen = null) {
    console.log("🚀 Iniciando envío a través del backend...");

    try {
      // Preparar datos para el backend
      const backendData = {
        nombre: contactData.nombre,
        email: contactData.email,
        telefono: contactData.telefono,
        mensaje: contactData.mensaje,
        // URL desde donde se envió el mensaje
        url_origen: window.location.href,
        // Origen del formulario (footer, proyecto, terreno, etc)
        origen_formulario: origen,
        // Datos adicionales de la propiedad (si existe)
        propiedad: propertyData
          ? {
              id: propertyData.id,
              nombre: propertyData.nombre,
              direccion: propertyData.direccion,
              localidad: propertyData.localidad,
              developmentId: propertyData.developmentId,
            }
          : null,
        // UTM params para tracking
        utm: this.getUTMParams(),
        timestamp: new Date().toISOString(),
      };

      console.log("📤 Enviando datos al backend:", backendData);
      console.log("🔗 URL:", this.getApiUrl());

      const response = await axios.post(this.getApiUrl(), backendData, {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 30000,
      });

      console.log("✅ Respuesta del backend:", response);

      // El backend debería devolver la respuesta de Tokko
      if (response.status === 200 || response.status === 201) {
        return {
          success: true,
          message:
            "Mensaje enviado correctamente. Nos pondremos en contacto a la brevedad.",
          data: response.data,
        };
      } else {
        console.warn(
          "⚠️ Respuesta inesperada:",
          response.status,
          response.data
        );
        return {
          success: false,
          message: `Error del servidor (${response.status}). Por favor, intente nuevamente.`,
          error: response.data,
        };
      }
    } catch (error) {
      console.error("❌ Error enviando al backend:", error);

      // Error claro para el usuario
      let errorMessage =
        "Error al enviar el mensaje. Por favor, intente nuevamente.";

      if (error.code === "ECONNABORTED") {
        errorMessage =
          "Timeout - El servidor tardó demasiado en responder. Por favor, intente nuevamente.";
      } else if (error.response) {
        errorMessage = `Error del servidor (${error.response.status}). Por favor, intente nuevamente.`;
      } else if (error.request) {
        errorMessage =
          "No se pudo conectar al servidor. Verifique su conexión a internet.";
      }

      return {
        success: false,
        message: errorMessage,
        error: error.message,
      };
    }
  }
}

TokkoService.UTM_KEYS = ["source", "medium", "campaign", "term", "content"];
TokkoService.UTM_STORAGE_KEY = "pbg_utm_params";

// Exportar una instancia de la clase para uso directo
const tokkoService = new TokkoService();
export default tokkoService;
